import { useMemo, useState } from "react";
import { orgChart, orgPhases, type OrgNode } from "@/data/orgChartData";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  Shield, ShieldAlert, Anchor, Wrench, Users, DollarSign, Scale, Crown,
  MapPin, Phone, Globe, ZoomIn, ZoomOut, Maximize2,
} from "lucide-react";

type DirectoryLite = {
  full_name: string;
  job_title: string;
  department: string;
  avatar_url: string | null;
  location: string | null;
  phone: string | null;
  bio: string | null;
  timezone: string | null;
};

interface Props {
  directory: DirectoryLite[];
}

const initials = (n: string) =>
  n.split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

const localTimeFor = (tz: string | null | undefined) => {
  if (!tz) return null;
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: tz, hour: "numeric", minute: "2-digit", hour12: true,
    }).format(new Date());
  } catch { return null; }
};

const iconFor = (node: OrgNode) => {
  if (node.id === "ceo") return Crown;
  if (node.id === "coo") return Anchor;
  if (node.id === "cco") return Users;
  if (node.id === "cfo") return DollarSign;
  if (node.id === "gc") return Scale;
  if (node.id === "hse") return ShieldAlert;
  if (node.division === "I") return Shield;
  if (node.division === "II") return Wrench;
  return Users;
};

const ringFor = (node: OrgNode) => {
  if (node.id === "ceo") return "ring-amber-400/70";
  if (node.reportsToCEO) return "ring-amber-400/60";
  if (node.division === "I") return "ring-teal/60";
  if (node.division === "II") return "ring-sky-400/60";
  if (["coo", "cco", "cfo", "gc"].includes(node.id)) return "ring-navy/40";
  return "ring-border";
};

const accentBarFor = (node: OrgNode) => {
  if (node.id === "ceo") return "bg-gradient-to-r from-amber-400 to-amber-500";
  if (node.reportsToCEO) return "bg-gradient-to-r from-amber-400 to-orange-400";
  if (node.division === "I") return "bg-gradient-to-r from-teal to-emerald-400";
  if (node.division === "II") return "bg-gradient-to-r from-sky-400 to-blue-500";
  if (["coo", "cco", "cfo", "gc"].includes(node.id)) return "bg-gradient-to-r from-navy to-slate-700";
  return "bg-muted-foreground/20";
};

const OrgChart = ({ directory }: Props) => {
  const [selected, setSelected] = useState<OrgNode | null>(null);
  const [zoom, setZoom] = useState(1);

  const directoryByName = useMemo(() => {
    const m = new Map<string, DirectoryLite>();
    directory.forEach((d) => m.set(d.full_name.toLowerCase().trim(), d));
    return m;
  }, [directory]);

  const profileFor = (node: OrgNode) =>
    node.name ? directoryByName.get(node.name.toLowerCase().trim()) : undefined;

  // ---------- Card ----------
  const NodeCard = ({
    node, size = "md",
  }: { node: OrgNode; size?: "sm" | "md" | "lg" }) => {
    const Icon = iconFor(node);
    const profile = profileFor(node);
    const time = localTimeFor(profile?.timezone);
    const isLg = size === "lg";
    const isSm = size === "sm";
    const avatarSize = isLg ? "h-14 w-14" : isSm ? "h-9 w-9" : "h-11 w-11";
    const widthCls = isLg ? "w-[280px]" : isSm ? "w-[200px]" : "w-[230px]";

    return (
      <button
        type="button"
        onClick={() => setSelected(node)}
        className={`group relative ${widthCls} text-left rounded-xl bg-white border border-border shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all overflow-hidden ring-1 ${ringFor(node)} ring-offset-0`}
      >
        {/* color accent bar */}
        <div className={`h-1 w-full ${accentBarFor(node)}`} />

        <div className={`flex items-start gap-3 ${isSm ? "p-2.5" : "p-3"}`}>
          <div className="relative flex-shrink-0">
            <Avatar className={`${avatarSize} ring-2 ring-white shadow-sm`}>
              {profile?.avatar_url ? (
                <AvatarImage src={profile.avatar_url} alt={node.name || node.title} />
              ) : null}
              <AvatarFallback className="bg-gradient-to-br from-navy/15 to-teal/15 text-navy font-display text-xs">
                {initials(node.name || node.title)}
              </AvatarFallback>
            </Avatar>
            <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white border border-border flex items-center justify-center shadow-sm">
              <Icon className="w-2.5 h-2.5 text-foreground/60" />
            </span>
          </div>

          <div className="min-w-0 flex-1">
            {node.name ? (
              <>
                <div className={`font-display ${isLg ? "text-sm" : "text-[13px]"} text-foreground leading-tight truncate group-hover:text-teal transition-colors`}>
                  {node.name}
                </div>
                <div className="font-body text-[11px] text-muted-foreground leading-tight truncate mt-0.5">
                  {node.title}
                </div>
              </>
            ) : (
              <div className={`font-display ${isLg ? "text-sm" : "text-[12px]"} text-foreground leading-tight group-hover:text-teal transition-colors line-clamp-2`}>
                {node.title}
              </div>
            )}

            {node.scope && !isSm && (
              <div className="font-body text-[9px] text-muted-foreground/70 uppercase tracking-wider mt-1 truncate">
                {node.scope}
              </div>
            )}

            {/* Inline contact strip when we have a directory match */}
            {profile && !isSm && (
              <div className="mt-1.5 flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] font-body text-muted-foreground">
                {profile.location && (
                  <span className="flex items-center gap-1"><MapPin className="w-2.5 h-2.5" />{profile.location}</span>
                )}
                {time && (
                  <span className="flex items-center gap-1"><Globe className="w-2.5 h-2.5" />{time}</span>
                )}
              </div>
            )}

            {node.flags && node.flags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {node.flags.map((f) => (
                  <Badge key={f} variant="outline" className="text-[9px] px-1.5 py-0 h-4 border-amber-400/60 text-amber-700 bg-amber-50/80">
                    {f}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </button>
    );
  };

  // ---------- Tree primitives ----------
  // Vertical connector between parent block and the row of children
  const VLine = ({ className = "" }: { className?: string }) => (
    <div className={`w-px bg-slate-300 mx-auto ${className}`} />
  );

  // Horizontal connector spanning siblings, with center stub up to parent
  const SiblingBus = ({ count }: { count: number }) =>
    count > 1 ? (
      <div className="relative h-6">
        {/* center stub up */}
        <div className="absolute left-1/2 top-0 w-px h-3 bg-slate-300" />
        {/* horizontal bar */}
        <div className="absolute top-3 left-[8%] right-[8%] h-px bg-slate-300" />
      </div>
    ) : (
      <VLine className="h-6" />
    );

  // Per-child stub down to the card
  const ChildStub = () => <div className="w-px h-3 bg-slate-300 mx-auto" />;

  // ---------- Tree assembly ----------
  const ceo = orgChart;
  const directReports = ceo.children ?? [];
  const coo = directReports.find((c) => c.id === "coo")!;
  const cco = directReports.find((c) => c.id === "cco")!;
  const cfo = directReports.find((c) => c.id === "cfo")!;
  const gc = directReports.find((c) => c.id === "gc")!;
  const hse = directReports.find((c) => c.id === "hse")!;

  const cSuite = [coo, cco, cfo, gc];
  const cooSpine = coo.children?.filter((c) => !["div-1", "div-2"].includes(c.id)) ?? [];
  const div1 = coo.children?.find((c) => c.id === "div-1");
  const div2 = coo.children?.find((c) => c.id === "div-2");

  const matched = selected ? profileFor(selected) : undefined;
  const selectedTime = localTimeFor(matched?.timezone);

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3 pb-3 border-b border-border">
        <div className="flex flex-wrap items-center gap-3 text-[11px] font-body text-muted-foreground">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-400" /> Executive / CEO line</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-navy" /> C-suite</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-teal" /> Division I — HiCap</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-sky-500" /> Division II — AIRCAT</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-orange-400" /> Independent assurance</span>
        </div>
        <div className="flex items-center gap-1 bg-muted/50 rounded-md p-0.5">
          <button onClick={() => setZoom((z) => Math.max(0.6, z - 0.1))} className="p-1.5 rounded hover:bg-background transition" title="Zoom out">
            <ZoomOut className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => setZoom(1)} className="p-1.5 rounded hover:bg-background transition text-[10px] font-mono w-10" title="Reset">
            {Math.round(zoom * 100)}%
          </button>
          <button onClick={() => setZoom((z) => Math.min(1.4, z + 0.1))} className="p-1.5 rounded hover:bg-background transition" title="Zoom in">
            <ZoomIn className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Tree canvas — pannable on mobile */}
      <div className="overflow-x-auto -mx-4 px-4 pb-4 bg-gradient-to-b from-slate-50/60 to-transparent rounded-lg">
        <div
          className="min-w-[1000px] mx-auto origin-top transition-transform"
          style={{ transform: `scale(${zoom})`, transformOrigin: "top center" }}
        >
          {/* Tier 1 — CEO with HSE on independent line beside it */}
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-6 mb-2">
            <div /> {/* spacer */}
            <div className="flex justify-center">
              <NodeCard node={ceo} size="lg" />
            </div>
            {/* Dotted/dashed line to HSE Manager (independent assurance) */}
            <div className="flex items-center gap-3 pl-2">
              <div className="h-px flex-1 border-t border-dashed border-amber-400/70" />
              <div className="flex flex-col items-start gap-1">
                <span className="text-[9px] font-body uppercase tracking-wider text-amber-700">Independent line</span>
                <NodeCard node={hse} />
              </div>
            </div>
          </div>

          {/* CEO → C-suite */}
          <VLine className="h-6" />
          <SiblingBus count={cSuite.length} />
          <div className="grid grid-cols-4 gap-4 px-4">
            {cSuite.map((n) => (
              <div key={n.id} className="flex flex-col items-center">
                <ChildStub />
                <NodeCard node={n} />
              </div>
            ))}
          </div>

          {/* COO branch — Operations spine + Divisions */}
          <div className="mt-4">
            <div className="flex items-center gap-2 mb-2">
              <Anchor className="w-3.5 h-3.5 text-navy" />
              <h3 className="font-display text-xs uppercase tracking-wider text-foreground">Operations spine — under COO</h3>
            </div>
            <VLine className="h-4" />
            <SiblingBus count={cooSpine.length} />
            <div className="grid grid-cols-4 gap-3 px-2">
              {cooSpine.map((n) => (
                <div key={n.id} className="flex flex-col items-center">
                  <ChildStub />
                  <NodeCard node={n} size="sm" />
                </div>
              ))}
            </div>
          </div>

          {/* Two divisions side-by-side */}
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            {[div1, div2].map((div) =>
              div ? (
                <div
                  key={div.id}
                  className={`rounded-xl p-4 ${
                    div.division === "I"
                      ? "bg-gradient-to-br from-teal/5 to-emerald-50/40 border border-teal/20"
                      : "bg-gradient-to-br from-sky-50/60 to-blue-50/40 border border-sky-300/30"
                  }`}
                >
                  <div className="flex justify-center mb-2">
                    <NodeCard node={div} size="lg" />
                  </div>
                  <VLine className="h-3" />
                  <SiblingBus count={div.children?.length ?? 0} />
                  <div className="grid grid-cols-3 gap-2">
                    {div.children?.map((n) => (
                      <div key={n.id} className="flex flex-col items-center">
                        <ChildStub />
                        <NodeCard node={n} size="sm" />
                      </div>
                    ))}
                  </div>
                </div>
              ) : null,
            )}
          </div>

          {/* Commercial spine */}
          <div className="mt-8">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-3.5 h-3.5 text-navy" />
              <h3 className="font-display text-xs uppercase tracking-wider text-foreground">Commercial spine — under CCO</h3>
            </div>
            <div className="grid grid-cols-4 gap-3 px-2">
              {cco.children?.map((n) => (
                <NodeCard key={n.id} node={n} size="sm" />
              ))}
            </div>
          </div>

          {/* Finance spine */}
          <div className="mt-6">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-3.5 h-3.5 text-navy" />
              <h3 className="font-display text-xs uppercase tracking-wider text-foreground">Finance & Admin — under CFO</h3>
            </div>
            <div className="grid grid-cols-3 gap-3 px-2">
              {cfo.children?.map((n) => (
                <NodeCard key={n.id} node={n} size="sm" />
              ))}
            </div>
          </div>

          {/* HSE function */}
          <div className="mt-6 rounded-xl p-4 bg-gradient-to-br from-amber-50/60 to-orange-50/30 border border-amber-300/40">
            <div className="flex items-center gap-2 mb-3">
              <ShieldAlert className="w-3.5 h-3.5 text-amber-600" />
              <h3 className="font-display text-xs uppercase tracking-wider text-foreground">
                HSE function — independent line to CEO
              </h3>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {hse.children?.map((n) => (
                <NodeCard key={n.id} node={n} size="sm" />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Phasing */}
      <div>
        <h3 className="font-display text-sm text-foreground mb-3">Phased build-out</h3>
        <div className="grid md:grid-cols-3 gap-3">
          {orgPhases.map((p, i) => (
            <Card key={p.id} className="overflow-hidden">
              <div className={`h-1 ${i === 0 ? "bg-teal" : i === 1 ? "bg-sky-500" : "bg-navy"}`} />
              <CardContent className="p-4">
                <div className="flex items-baseline justify-between mb-1">
                  <span className="font-display text-xs uppercase tracking-wider text-teal">{p.label}</span>
                  <span className="font-body text-[10px] text-muted-foreground">{p.horizon}</span>
                </div>
                <div className="font-display text-sm text-foreground mb-1">{p.title}</div>
                <div className="font-body text-[10px] text-muted-foreground mb-2">{p.headcount}</div>
                <ul className="space-y-1.5 font-body text-[11px] text-foreground/80 list-disc pl-4">
                  {p.points.map((pt) => <li key={pt}>{pt}</li>)}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="sm:max-w-lg p-0 overflow-hidden">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="sr-only">{selected.title}</DialogTitle>
              </DialogHeader>

              <div className={`h-2 ${accentBarFor(selected)}`} />

              <div className="px-6 pt-5 pb-4 flex items-start gap-4 border-b border-border">
                <Avatar className={`h-20 w-20 ring-2 ring-white shadow-md ${ringFor(selected)} ring-offset-2`}>
                  {matched?.avatar_url ? (
                    <AvatarImage src={matched.avatar_url} alt={selected.name || selected.title} />
                  ) : null}
                  <AvatarFallback className="bg-gradient-to-br from-navy/15 to-teal/15 text-navy font-display text-xl">
                    {initials(selected.name || selected.title)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  {selected.name && selected.name !== "To be appointed" && !selected.name.toLowerCase().includes("fractional") ? (
                    <>
                      <h2 className="font-display text-xl text-foreground leading-tight">{selected.name}</h2>
                      <p className="font-body text-sm text-muted-foreground mt-0.5">{selected.title}</p>
                    </>
                  ) : (
                    <h2 className="font-display text-xl text-foreground leading-tight">{selected.title}</h2>
                  )}
                  {selected.scope && (
                    <p className="font-body text-[11px] uppercase tracking-wider text-muted-foreground/80 mt-1">
                      {selected.scope}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {selected.spine && <Badge variant="secondary" className="text-[10px]">{selected.spine}</Badge>}
                    {selected.division && (
                      <Badge variant="outline" className="text-[10px]">
                        {selected.division === "I" ? "Division I — HiCap" : "Division II — AIRCAT"}
                      </Badge>
                    )}
                    {selected.flags?.map((f) => (
                      <Badge key={f} variant="outline" className="text-[10px] border-amber-400/60 text-amber-700 bg-amber-50">
                        {f}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 space-y-4 max-h-[55vh] overflow-y-auto">
                {selected.responsibilities && selected.responsibilities.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">
                      Responsibilities
                    </p>
                    <ul className="space-y-1.5 font-body text-sm text-foreground/90 list-disc pl-5">
                      {selected.responsibilities.map((r) => <li key={r}>{r}</li>)}
                    </ul>
                  </div>
                )}

                {selected.phaseNote && (
                  <div className="rounded-lg border border-amber-300/60 bg-amber-50/60 p-3">
                    <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-1">Phasing note</p>
                    <p className="font-body text-xs text-amber-900/90">{selected.phaseNote}</p>
                  </div>
                )}

                {matched ? (
                  <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                      Directory profile — {matched.department}
                    </p>
                    {matched.bio && (
                      <p className="font-body text-xs text-foreground/90 leading-relaxed">{matched.bio}</p>
                    )}
                    <div className="grid sm:grid-cols-2 gap-2 font-body text-xs text-foreground/80">
                      {matched.location && (
                        <span className="flex items-center gap-2"><MapPin className="w-3 h-3 text-muted-foreground" />{matched.location}</span>
                      )}
                      {matched.phone && (
                        <a href={`tel:${matched.phone}`} className="flex items-center gap-2 hover:text-teal">
                          <Phone className="w-3 h-3 text-muted-foreground" />{matched.phone}
                        </a>
                      )}
                      {matched.timezone && (
                        <span className="flex items-center gap-2">
                          <Globe className="w-3 h-3 text-muted-foreground" />
                          {matched.timezone.replace(/_/g, " ")}{selectedTime ? ` · ${selectedTime}` : ""}
                        </span>
                      )}
                    </div>
                  </div>
                ) : selected.name && selected.name !== "To be appointed" && !selected.name.toLowerCase().includes("fractional") ? (
                  <p className="font-body text-[11px] text-muted-foreground italic">
                    No directory profile linked to {selected.name} yet — add them in the Company Directory to surface their photo and contact details here.
                  </p>
                ) : selected.name === "To be appointed" ? (
                  <p className="font-body text-[11px] text-muted-foreground italic">
                    Seat open — pending hire per the phased build-out plan.
                  </p>
                ) : null}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrgChart;
